import React from 'react';
import TermsLayout from '../components/TermsLayout';

function AcceptableUse() {
  const sections = [
    {
      title: "1. Purpose and Scope",
      content: `This Acceptable Use Policy ("AUP") governs your use of Zella's workplace wellbeing platform, including the mobile application and web dashboard.

This AUP is incorporated into and forms part of our Terms of Service. Violations of this AUP may result in account suspension or termination.

Attempting, encouraging, assisting, or facilitating any prohibited activity is itself a violation of this Policy, regardless of whether the prohibited act was completed.

Users may not attempt to circumvent the intent of this Policy through technical workarounds, organisational arrangements, indirect actions, or use of third parties.

Who This Applies To:
- All mobile app users (employees)
- All web dashboard users (administrators)
- All organisation representatives
- Any person accessing Zella services

Our Goal:
To maintain a safe, secure, and productive platform that respects the privacy and wellbeing of all users while advancing workplace safety research.`
    },
    {
      title: "2. Permitted Uses",
      content: `Mobile App Users (Employees) may:
- Submit honest and accurate wellbeing surveys
- Track survey submission history
- View personal tiredness scores
- Access wellbeing resources and information
- Report bugs or provide feedback about the app

Dashboard Users (Administrators) may:
- Manage team structures and memberships
- Approve employee access requests
- View aggregated team-level analytics
- Generate reports for workplace safety and wellbeing purposes
- Manage organisation settings and preferences
- Export anonymised team data for internal analysis
- Compare team wellbeing trends and aggregated wellbeing indicators

Organisations may use Zella to:
- Monitor workplace wellbeing trends
- Identify team-level fatigue patterns
- Improve workplace safety policies
- Conduct internal research on employee wellbeing
- Comply with occupational health and safety requirements`
    },
    {
      title: "3. Prohibited Uses — General",
      content: `You may NOT use Zella for:

Illegal Activities:
- Any activity that violates local, national, or international law
- Facilitating illegal conduct
- Money laundering or financial fraud
- Terrorism or violent extremism
- Child exploitation or endangerment

Harmful or Abusive Conduct:
- Harassing, threatening, or intimidating other users
- Stalking or revealing private information about others
- Impersonating others or creating fake accounts
- Defamation or spreading false information about others
- Hate speech or discrimination based on protected characteristics

Security and System Integrity:
- Hacking, cracking, or unauthorised access attempts
- Introducing viruses, malware, or malicious code
- Attempting to bypass security measures
- Distributed denial-of-service (DDoS) attacks
- Scanning or probing for system vulnerabilities
- Reverse engineering or decompiling Zella software

Intellectual Property Violations:
- Copying, distributing, or modifying Zella's software
- Using Zella's trademarks without permission
- Creating derivative works
- Selling or reselling access to Zella
- Using Zella to infringe others' intellectual property

Data Scraping and Automation:
- Automated data extraction (scraping, crawling, harvesting)
- Using bots or scripts to access Zella
- Mass data collection beyond intended functionality
- Creating automated accounts
- Circumventing rate limits or access controls

Privacy Violations:
- Attempting to identify individual survey responses
- De-anonymising aggregated data
- Accessing data you are not authorised to view
- Violating data protection laws (UK GDPR, CCPA, or other applicable law)

Platform Abuse:
- Spamming or sending unsolicited communications
- Creating multiple accounts to evade restrictions
- Circumventing account suspensions or bans
- Manipulating survey responses or data
- Artificially inflating or deflating metrics
- Using Zella for purposes other than workplace wellbeing monitoring`
    },
    {
      title: "4. Prohibited Uses — Survey Data and Anonymity",
      content: `Protecting the privacy of employee survey responses is central to Zella's purpose. The following are strictly prohibited and may result in immediate account termination.

Mobile App Users Must NOT:
- Submit false or deliberately misleading survey responses
- Submit surveys on behalf of another person
- Share account credentials with others
- Coordinate survey responses with coworkers to skew team data
- Use automated tools to submit surveys

Administrator Users Must NOT:

Identification attempts:
- Attempt to identify which employee submitted which survey response
- Use timestamps, submission patterns, or other metadata to deduce individual identities
- Combine Zella data with other internal systems or external data sources to re-identify employees
- Introduce synthetic, automated, or bot accounts into teams to manipulate group composition or attempt to isolate real survey responses
- Observe or record when employees submit their surveys for the purpose of linking responses to individuals
- Require employees to complete surveys at specific times, in specific sequences, or under observation for the purpose of linking responses to individuals

Team composition manipulation:
- Intentionally manipulate team composition, approval status, team assignments, or organisational structures for the purpose of increasing the likelihood of identifying individual survey responses
- Move employees between teams in a pattern designed to isolate survey responses to specific individuals

Misuse of analytics:
- Represent Zella scores, analytics, or fatigue indicators as medical diagnoses, clinical assessments, or definitive measures of fitness for work
- Use Zella data as the sole or primary basis for individual employment decisions (see Section 4A)
- Share team data with unauthorised parties
- Export data without proper security safeguards
- Pressure employees to submit surveys with specific responses
- Misrepresent to employees how survey data is used or what administrators can see

Any harm arising from conduct prohibited under this section is the sole liability of the customer. Zella accepts no responsibility for privacy failures that result from customer conduct.

Research Data Users Must NOT:
- Attempt to re-identify companies, teams, or individuals
- Use data for employment screening, insurance underwriting, or marketing
- Combine datasets with other sources to break anonymisation
- Share datasets with unauthorised third parties
- Publish findings that could identify specific companies`
    },
    {
      title: "4A. Prohibited Employment Uses",
      content: `This section addresses Zella's most significant misuse risk — the use of aggregated wellbeing data to make or justify individual employment decisions.

Zella is an aggregated workplace wellbeing tool. It is not an individual employee assessment system. Organisations must not use Zella data:

- As the sole or primary basis for disciplinary action against an individual
- As the sole or primary basis for dismissal decisions
- As the sole or primary basis for promotion or demotion decisions
- As the sole or primary basis for compensation decisions
- As the sole or primary basis for fitness-for-work determinations
- As the sole or primary basis for any staffing or scheduling decision affecting a specific individual
- As evidence in employment tribunal proceedings without appropriate expert context
- To justify any individual employment decision where the underlying data cannot be attributed to that individual

Zella provides team-level aggregated information only. Individual survey responses are not accessible through the platform. Any inference about an individual's performance, health, or fitness based on team averages reflects the organisation's own assumptions, not Zella data, and is the organisation's sole responsibility.

Organisations are responsible for ensuring any use of Zella data in an employment context complies with applicable employment law, data protection law, and any relevant equality legislation.`
    },
    {
      title: "5. Data Protection Obligations",
      content: `All Users Must:
- Comply with UK GDPR, the Data Protection Act 2018, and all other applicable privacy laws
- Not attempt to access others' personal information
- Report suspected privacy breaches to privacy@zellawellbeing.com
- Maintain confidentiality of company access codes (administrators)

Administrators Must Additionally:

Lawful Basis and Employee Notice:
- Inform employees about Zella's use and data practices before or at the point of deployment
- Provide or rely on a valid lawful basis for data processing under UK GDPR
- Ensure any participation requirements comply with applicable employment and data protection laws
- Respect employee rights to access, correct, or request deletion of data

Protect Exported Data:
- Once data is exported from Zella, the organisation is solely responsible for its security, storage, transmission, retention, and lawful use
- Limit access to exported data to authorised personnel only
- Delete exported data when no longer needed for its stated purpose
- Not share exported data with unauthorised third parties

Use Data Ethically:
- Only for legitimate workplace safety and wellbeing purposes
- Not for discriminatory purposes
- Not for individual performance management
- Transparently with employees about data usage

Note on Employer Obligations as Data Controller:

By deploying Zella to employees, your organisation takes on the role of data controller for employee survey data. You are responsible for ensuring your use of Zella complies with UK GDPR, including maintaining a valid lawful basis, providing appropriate employee notice, responding to data subject rights requests, and conducting a data protection impact assessment if required. Zella acts as your data processor as governed by our Data Processing Agreement.`
    },
    {
      title: "6. Account Security Obligations",
      content: `You are responsible for:
- Creating strong, unique passwords and not sharing them with anyone
- Logging out from shared devices
- Not allowing others to use your account
- Monitoring your account for unauthorised activity
- Immediately reporting suspected account compromise to legal@zellawellbeing.com
- Enabling two-factor authentication when available

Administrators are additionally responsible for:
- Restricting dashboard access to authorised personnel only
- Regularly reviewing who has administrator access
- Revoking access promptly for former employees
- Securing company access codes — do not share them publicly or irresponsibly`
    },
    {
      title: "7. Content Standards",
      content: `When providing feedback, bug reports, or communications through Zella:

You Must NOT include:
- Profanity, obscenity, or vulgar language
- Sexually explicit content
- Violent or graphic content
- Hate speech or discriminatory content
- Personal attacks or harassment
- Spam or advertising
- Special category personal data (as defined by UK GDPR Article 9, including health data, racial or ethnic origin, religious beliefs, sexual orientation, biometric data) unless specifically requested as part of a support process
- Confidential or proprietary information of third parties without authorisation
- False or misleading information

You Must:
- Be respectful and professional
- Provide constructive and accurate feedback
- Respect others' privacy
- Comply with applicable laws`
    },
    {
      title: "8. Prohibited Third-Party Services",
      content: `You may not use Zella in conjunction with:

Unauthorised Integrations:
- Third-party apps not approved by Zella
- Unofficial API clients or wrappers
- Browser extensions that modify Zella's interface or behaviour
- Automation tools or bots

Harmful Services:
- VPNs or proxies used specifically to evade Zella's access restrictions or account bans
- Services that facilitate violations of these Terms
- Services that compromise the security or privacy of other users`
    },
    {
      title: "9. Reporting Violations",
      content: `If you witness AUP violations, please report to:
- Security vulnerabilities: security@zellawellbeing.com
- Privacy breaches: privacy@zellawellbeing.com
- Terms or AUP violations: legal@zellawellbeing.com
- Harassment or abuse: use website contact form

When reporting, include a description of the violation, when and where it occurred, and any supporting evidence.

We will investigate all reports promptly and maintain confidentiality of reporters where possible. Good faith reports are protected.

Knowingly submitting false, misleading, or bad-faith reports regarding other users is itself a violation of this Policy and may result in action against the reporter.`
    },
    {
      title: "10. Consequences of Violations and Suspension Rights",
      content: `Zella may suspend or restrict access immediately, without prior notice, where reasonably necessary to:
- Investigate suspected violations of this Policy or the Terms of Service
- Protect the security or integrity of the platform
- Protect the privacy of users or data subjects
- Comply with legal obligations
- Prevent harm to other users or third parties

This right to suspend is in addition to, and does not replace, any other remedies available to Zella.

Violation of this AUP may result in:
- Warning and request to cease violation
- Temporary account suspension
- Permanent account termination without refund
- Removal of violating content or access
- Referral to law enforcement
- Legal action for damages

Factors we consider: severity of violation, intent (deliberate vs. accidental), history of violations, and harm caused.

Appeal Process:

If your account is suspended or terminated:
1. Email legal@zellawellbeing.com within 14 days
2. Explain your perspective on the situation
3. Provide any mitigating evidence
4. Propose resolution if applicable

We will review appeals within 30 days. We may reinstate accounts where violations were accidental or minor, the issue has been resolved, and you agree to comply going forward. We are not obligated to reinstate accounts and may decline at our discretion.`
    },
    {
      title: "11. Administrator-Specific Obligations",
      content: `Additional requirements for organisation administrators:

Workforce Communication:
- Clearly explain Zella's purpose to employees before deployment
- Provide or reference Zella's Privacy Policy and Terms of Service
- Inform employees that survey data is aggregated — administrators see team averages, not individual responses
- Explain how team data will and will not be used
- Not misrepresent Zella's capabilities or data practices
- Not misrepresent to employees that survey responses are fully anonymous — administrators should state that responses are not individually visible through the dashboard

Ethical Data Use:
- Use team data only for workplace safety and wellbeing purposes
- Not use data to attempt to identify individual employees
- Not retaliate against teams or individuals based on survey patterns
- Not use data for individual performance reviews, disciplinary actions, or employment decisions (see Section 4A)
- Not share data with unauthorised parties

Employee Rights:
- Respect employees' data protection rights
- Facilitate employee data access requests where possible
- Honour employee objections where legally required
- Ensure any participation requirements comply with applicable employment and data protection laws

Compliance:
- Comply with employment laws regarding workplace monitoring
- Comply with data protection regulations
- Maintain documentation of lawful basis for processing
- Conduct data protection impact assessments if required for your scale and type of processing

Prohibited Administrator Actions:
- Attempting to identify employees through process of elimination, timing analysis, or metadata analysis
- Using Zella for surveillance purposes beyond its intended wellbeing function
- Manipulating team composition for the purpose of identifying individual survey responses
- Failing to secure exported data`
    },
    {
      title: "12. Research and Academic Use",
      content: `For researchers accessing anonymised aggregated datasets from Zella:

Permitted Research Uses:
- Workplace safety and wellbeing research
- Public health studies
- Academic research and publications
- Industry benchmarking and analysis
- Policy development research

Required Standards:
- Evidence of ethics approval where appropriate (for example, institutional ethics board approval for academic research)
- Signed Data Sharing Agreement with Zella
- Compliance with research ethics standards
- Protection of anonymised data
- No re-identification attempts

Prohibited Research Uses:
- Attempting to identify individuals, companies, or teams
- Using data for commercial purposes beyond agreed research scope
- Sharing datasets with unauthorised parties
- Employment screening or background checks
- Insurance underwriting or pricing
- Marketing to identified groups
- Combining with other datasets to break anonymisation

Publication Requirements:
- Do not reveal company identities in publications
- Use only aggregated statistics in publications
- Acknowledge data source appropriately
- Follow academic integrity and research ethics standards

Data Security for Researchers:
- Store data securely with encryption
- Limit access to authorised researchers only
- Destroy data after research completion per the Data Sharing Agreement
- Report any data breaches immediately to privacy@zellawellbeing.com`
    },
    {
      title: "13. Cooperation and Monitoring",
      content: `Our Right to Monitor:

We reserve the right to:
- Monitor use of Zella for Terms and AUP compliance
- Review reported violations
- Investigate suspicious activity
- Access accounts where necessary to investigate violations
- Cooperate with law enforcement and regulatory authorities
- Preserve evidence of violations

Monitoring activities will be conducted proportionately and in accordance with applicable privacy and data protection laws.

Automated Monitoring:
We use automated systems to detect abuse, including rate limiting and Firebase App Check. Unusual activity may trigger security reviews.

Your Cooperation:

If we contact you about suspected violations:
- Respond promptly to our enquiries
- Provide requested information
- Cooperate with investigations
- Cease violating activity immediately

Failure to cooperate may result in account suspension or termination.

Legal Cooperation:

We cooperate with law enforcement investigations, court orders and subpoenas, regulatory enquiries, and data protection authority investigations. We may disclose information as required by law or to protect our rights and the rights of others.`
    },
    {
      title: "14. Updates to This Policy",
      content: `We may update this policy to address new abuse patterns, comply with legal requirements, or improve clarity.

Notification of Changes:
- Material changes: 30 days notice via email
- Minor changes: posted to website

Continued use after changes constitutes acceptance. Contact legal@zellawellbeing.com with any questions.`
    },
    {
      title: "15. Contact and Questions",
      content: `General AUP Questions:
Email: legal@zellawellbeing.com

Security Concerns (responsible disclosure of vulnerabilities):
Email: security@zellawellbeing.com

Privacy Questions:
Email: privacy@zellawellbeing.com

Report Violations:
- Terms or AUP violations: legal@zellawellbeing.com
- Security issues: security@zellawellbeing.com
- Harassment or abuse: use website contact form

We welcome feedback on this policy — contact legal@zellawellbeing.com with suggestions for improvement.

Response Time: We aim to respond to all AUP enquiries within 5 business days.`
    }
  ];

  const summary = {
    title: "Quick Summary",
    content: `This Acceptable Use Policy defines what you can and cannot do with Zella.

Permitted:
- Honest survey submissions
- Viewing aggregated team wellbeing trends (admins)
- Using data for legitimate workplace safety and wellbeing purposes

Strictly Prohibited:
- Introducing bot or synthetic accounts into teams to manipulate anonymity
- Manipulating team composition to isolate individual responses
- Requiring employees to submit surveys under observation or at specific times to identify responses
- Observing employees submit surveys to identify individual responses
- Using Zella scores as the sole basis for disciplinary, dismissal, or fitness-for-work decisions
- Representing Zella scores as medical diagnoses or clinical assessments
- Combining Zella data with other sources to de-anonymise individuals
- Submitting false survey data
- Hacking, scraping, or system abuse
- Attempting or facilitating any prohibited activity

Consequences: Violations may result in immediate suspension or termination. Harm caused by prohibited conduct is the customer's liability, not Zella's.

Questions? Contact legal@zellawellbeing.com`
  };

  return (
    <TermsLayout>
      <h1>Acceptable Use Policy</h1>
      <p className="last-updated">Last Updated: June 2026</p>

      {sections.map((section, index) => (
        <section key={index}>
          <h2>{section.title}</h2>
          <p style={{ whiteSpace: 'pre-line' }}>{section.content}</p>
        </section>
      ))}

      <section className="summary-section">
        <h2>{summary.title}</h2>
        <p style={{ whiteSpace: 'pre-line' }}>{summary.content}</p>
      </section>

      <footer className="policy-footer">
        <p><strong>Version: 3.0</strong></p>
        <p>Effective Date: June 2026</p>
        <p><em>Zella — Workplace Wellbeing Monitoring</em></p>
      </footer>
    </TermsLayout>
  );
}

export default AcceptableUse;